import {
  AppShell,
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  Paper,
  Skeleton,
  Table,
  Text,
} from "@mantine/core";
import {
  GetVentsResponseRecord,
  useGetVentsQuery,
} from "../slices/api/ventsApi";
import { JSX, useEffect, useState } from "react";
import dayjs from "dayjs";
import { useDisclosure } from "@mantine/hooks";
import CreateVent from "./CreateVent";
import { useLocation } from "wouter";
import { TextSearchBox } from "../shared/components/TextSearchBox";
import {
  IconAddNew,
  IconClone,
  IconConfigureEvent,
  IconDelete,
  IconGuests,
  IconActivities,
} from "../shared/components/Icons";
import { useAppDispatch, useAppSelector } from "../hooks";
import { RootState } from "../store";
import { initialize, updateField } from "../slices/form/ventsTab";
import DeleteVent from "./DeleteVent";
import CloneVent from "./CloneVent";
import { useGetUserAttributesQuery } from "../slices/api/usersApi";
import { handleFetchingError } from "../shared/ErrorMessage";
import { EbTooltip } from "../shared/components/EbTooltip";
import { EbActionIcon } from "../shared/components/EbActionItem";
import { LinkAnchorToVentHome } from "../shared/components/LinkAnchors";
import {
  getLinkToVentConfig,
  getLinkToVentGuests,
  getLinkToVentActivities,
} from "../shared/links";

function VentsTab() {
  const { data } = useAppSelector((state: RootState) => state.ventsTab);

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (data) return; // prevent reset when re-mounting component
    dispatch(
      initialize({
        includeEnded: false,
        searchText: "",
      })
    );
  }, []);
  const [_, navigate] = useLocation();

  const [openedCreateVent, { open: openCreateVent, close: closeCreateVent }] =
    useDisclosure(false);
  const [openedDeleteVent, { open: openDeleteVent, close: closeDeleteVent }] =
    useDisclosure(false);
  const [openedCloneVent, { open: openCloneVent, close: closeCloneVent }] =
    useDisclosure(false);
  const {
    data: ventsResponse,
    isFetching: isFetchingGetVents,
    isSuccess: isSuccessGetVents,
    isError: isErrorFetchingVents,
    error: ventsError,
  } = useGetVentsQuery({ includeEnded: data?.includeEnded ?? false });

  const [currentVent, setCurrentVent] = useState<GetVentsResponseRecord>();
  const { data: userAttributes } = useGetUserAttributesQuery({});

  if (isErrorFetchingVents) {
    handleFetchingError(ventsError);
    return null;
  }
  if (!ventsResponse || !data) {
    return (
      <AppShell.Main>
        <Loader color="blue" />
      </AppShell.Main>
    );
  }

  let rows: JSX.Element[] = [];
  if (isSuccessGetVents && ventsResponse?.vents.length > 0) {
    const filteredVents = ventsResponse.vents.filter((vent) =>
      vent.name.toLowerCase().includes(data.searchText.toLowerCase())
    );
    const sorted = filteredVents.sort((a, b) => (a.starts < b.starts ? 1 : -1));
    rows = sorted.map((vent) => (
      <Table.Tr key={vent.id}>
        <Table.Td>
          <Group gap="0.5rem">
            <EbActionIcon
              aria-label="Event configuration"
              onClick={() => {
                navigate(getLinkToVentConfig(vent.id));
              }}
            >
              <EbTooltip label="Configuration">
                <IconConfigureEvent />
              </EbTooltip>
            </EbActionIcon>
            <EbActionIcon
              aria-label="Guests"
              onClick={() => {
                navigate(getLinkToVentGuests(vent.id));
              }}
            >
              <EbTooltip label="Guests">
                <IconGuests />
              </EbTooltip>
            </EbActionIcon>
            <EbActionIcon
              aria-label="Activities"
              onClick={() => {
                navigate(getLinkToVentActivities(vent.id));
              }}
            >
              <EbTooltip label="Activities">
                <IconActivities />
              </EbTooltip>
            </EbActionIcon>
            <EbActionIcon
              disabled={!vent.canUserClone}
              aria-label="Clone"
              onClick={() => {
                setCurrentVent(vent);
                openCloneVent();
              }}
            >
              <EbTooltip label="Clone">
                <IconClone />
              </EbTooltip>
            </EbActionIcon>
            <EbActionIcon
              disabled={!vent.canUserDelete}
              aria-label="Delete event"
              onClick={() => {
                setCurrentVent(vent);
                openDeleteVent();
              }}
            >
              <EbTooltip label="Delete event">
                <IconDelete />
              </EbTooltip>
            </EbActionIcon>
          </Group>
        </Table.Td>
        <Table.Td>
          <LinkAnchorToVentHome ventId={vent.id}>
            {vent.name}
          </LinkAnchorToVentHome>
        </Table.Td>
        <Table.Td>{dayjs(vent.starts).format("LLL")}</Table.Td>
        <Table.Td>{dayjs(vent.ends).format("LLL")}</Table.Td>
        <Table.Td>{dayjs(vent.updateTime).format("LLL z")}</Table.Td>
      </Table.Tr>
    ));
  }

  return (
    <AppShell.Main>
      <Modal
        opened={openedCreateVent}
        onClose={closeCreateVent}
        withCloseButton={false}
        centered
      >
        <CreateVent onClose={closeCreateVent} />
      </Modal>
      <Modal
        opened={openedDeleteVent}
        onClose={closeDeleteVent}
        withCloseButton={false}
        centered
      >
        <DeleteVent
          onClose={closeDeleteVent}
          updateTime={currentVent?.updateTime ?? ""}
          ventEnds={currentVent?.ends ?? ""}
          ventId={currentVent?.id ?? ""}
          ventName={currentVent?.name ?? ""}
        />
      </Modal>
      <Modal
        opened={openedCloneVent}
        onClose={closeCloneVent}
        withCloseButton={false}
        centered
      >
        <CloneVent
          onClose={closeCloneVent}
          ventId={currentVent?.id ?? ""}
          ventName={currentVent?.name ?? ""}
          ventStarts={currentVent?.starts ?? ""}
        />
      </Modal>
      <Group>
        <Text mb="0" size="xl">
          Events as Host
        </Text>
        <Button
          onClick={openCreateVent}
          rightSection={<IconAddNew />}
          disabled={!userAttributes?.canOwnVents}
        >
          Add Event
        </Button>
      </Group>
      <TextSearchBox
        setSearchText={(searchText) => {
          dispatch(
            updateField({
              field: "searchText",
              value: searchText,
            })
          );
        }}
        searchText={data.searchText}
      />
      <Paper>
        <Group mt="sm">
          <Checkbox
            label={`Include ended events`}
            checked={data.includeEnded}
            onChange={(event) =>
              dispatch(
                updateField({
                  field: "includeEnded",
                  value: event.currentTarget.checked,
                })
              )
            }
          />
        </Group>
      </Paper>
      <Skeleton visible={isFetchingGetVents}>
        <div hidden={!isSuccessGetVents || rows.length === 0}>
          <Table stickyHeader stickyHeaderOffset={0}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Actions</Table.Th>
                <Table.Th>Event</Table.Th>
                <Table.Th>Starts</Table.Th>
                <Table.Th>Ends</Table.Th>
                <Table.Th>Updated</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </div>
        <div hidden={isFetchingGetVents || rows.length > 0}>
          No events displayed. Change search or add an event.
        </div>
      </Skeleton>
    </AppShell.Main>
  );
}

export default VentsTab;
